import React from "react";
import { Helmet } from "react-helmet";

const BRAND = process.env.REACT_APP_WEBSITE_TITLE || "X33";

const DEFAULT_SEO = {
  title: `${BRAND} | Online Casino Malaysia`,
  description: `Explore live casino games, slots, sports betting, promotions and account features available to Malaysian players on ${BRAND}.`,
};

const PAGE_SEO = {
  "/": DEFAULT_SEO,
  "/online-live-games-malaysia": {
    title: `Live Casino Malaysia | ${BRAND}`,
    description: `Explore live casino games available to Malaysian players on ${BRAND}, including table game options designed for desktop and mobile play.`,
  },
  "/e-sports": {
    title: `E-Sports Betting | ${BRAND}`,
    description: `View e-sports betting markets on ${BRAND}, with match selections and event information for competitive gaming titles in one place.`,
  },
  "/promos": {
    title: `Promotions | ${BRAND}`,
    description: `Browse current ${BRAND} promotions, review the eligibility requirements and understand the applicable terms before choosing an offer.`,
  },
  "/vip": {
    title: `VIP Programme | ${BRAND}`,
    description: `Learn how the ${BRAND} VIP Programme works, including its membership levels, available benefits and qualification requirements.`,
  },
  "/about-us": {
    title: `About ${BRAND}`,
    description: `Learn about ${BRAND}, the gaming categories available on the platform and the services provided to registered account holders.`,
  },
  "/contact-us": {
    title: `Contact Us | ${BRAND}`,
    description: `Contact the ${BRAND} support team for help with your account, payments or platform questions through the available support channels.`,
  },
  "/info-centre": {
    title: `Info Centre | ${BRAND}`,
    description: `Visit the ${BRAND} Info Centre for guidance about accounts, payments, game categories, platform features and responsible play.`,
  },
  "/download-app": {
    title: `Download the ${BRAND} App`,
    description: `Download the ${BRAND} app for supported mobile devices and follow the installation guidance to access platform features on the go.`,
  },
  "/404": {
    title: `Page Not Found | ${BRAND}`,
    description: `The page you requested could not be found. Return to ${BRAND} or contact the support team if you need help finding information.`,
  },
  "/online-sports-games-malaysia": {
    title: `Sports Betting Malaysia | ${BRAND}`,
    description: `Explore sports betting markets available to Malaysian players on ${BRAND}, with event information and selections across popular sports.`,
  },
  "/slot": {
    title: `Online Slots Malaysia | ${BRAND}`,
    description: `Browse online slot games available to Malaysian players on ${BRAND}, with a range of providers and titles for desktop and mobile play.`,
  },
  "/games": {
    title: `Online Games | ${BRAND}`,
    description: `Browse online games available on ${BRAND}, compare participating providers and choose a game category for desktop or mobile play.`,
  },
  "/p2p": {
    title: `P2P Games | ${BRAND}`,
    description: `Explore peer-to-peer games available on ${BRAND}, with participating providers and game options presented in one dedicated category.`,
  },
  "/lottery": {
    title: `Online Lottery | ${BRAND}`,
    description: `Explore online lottery games available on ${BRAND}, browse participating providers and review the available game options in one place.`,
  },
  "/speed": {
    title: `Speed Games | ${BRAND}`,
    description: `Browse speed games available on ${BRAND}, compare participating providers and select from the game options offered for desktop and mobile play.`,
  },
  "/certificate": {
    title: `Certificates | ${BRAND}`,
    description: `Review the certificates and regulatory information presented by ${BRAND}, including the supporting details published for platform visitors.`,
  },
  "/responsible-gaming": {
    title: `Responsible Gaming | ${BRAND}`,
    description: `Read ${BRAND}'s responsible gaming guidance, including practical account controls, safer play information and sources of additional support.`,
  },
  "/privacy-policy": {
    title: `Privacy Policy | ${BRAND}`,
    description: `Read the ${BRAND} Privacy Policy to understand how personal information is collected, used, protected and managed across the platform.`,
  },
};

const VENDOR_NAMES = {
  ag: "Yggdrasil",
  evo: "Evolution Gaming",
  gp: "GamePlay Interactive",
  isb: "iSoftBet",
  live22: "Live22",
  mg: "Microgaming",
  net: "NetEnt",
  pg: "Pocket Games Soft",
  pop: "PP POP",
  pp: "Pragmatic Play",
  qt: "QTech",
  sbo: "SBOBet",
  sg: "Spadegaming",
  sp: "SimplePlay",
  ttg: "TopTrend Gaming",
};

const normalizePath = (pathname = "/") => {
  const path = pathname.split("?")[0].split("#")[0];
  return path.length > 1 ? path.replace(/\/+$/, "") : "/";
};

const VENDOR_PATH_PATTERN = /^\/(slot|speed|games|p2p|lottery)\/([^/]+)$/;

export const isIndexablePath = (pathname) => {
  const path = normalizePath(pathname);
  return (path !== "/404" && Boolean(PAGE_SEO[path])) || VENDOR_PATH_PATTERN.test(path);
};

export const getSeoForPath = (pathname) => {
  const path = normalizePath(pathname);
  const staticSeo = PAGE_SEO[path];

  if (staticSeo) {
    return staticSeo;
  }

  const vendorMatch = path.match(VENDOR_PATH_PATTERN);
  if (vendorMatch) {
    const category = vendorMatch[1];
    const slug = vendorMatch[2].toLowerCase();
    const vendor = VENDOR_NAMES[slug] || slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    const categoryNames = {
      games: "Games",
      lottery: "Lottery Games",
      p2p: "P2P Games",
      slot: "Slots",
      speed: "Speed Games",
    };
    const categoryName = categoryNames[category];

    return {
      title: `${vendor} ${categoryName} | ${BRAND}`,
      description: `Explore ${vendor} ${categoryName.toLowerCase()} on ${BRAND}, with titles available for desktop and mobile play in the provider catalogue for Malaysian players.`,
    };
  }

  return DEFAULT_SEO;
};

const getSiteUrl = () => {
  const configuredUrl = process.env.REACT_APP_PUBLIC_URL;
  const fallbackUrl = "https://www.x33my3.com";
  return (configuredUrl || fallbackUrl).replace(/\/+$/, "");
};

const SeoMeta = ({
  title = DEFAULT_SEO.title,
  description = DEFAULT_SEO.description,
  pathname = "/",
  canonical,
  image,
  imageWidth = 600,
  imageHeight = 315,
  noindex = false,
}) => {
  const siteUrl = getSiteUrl();
  const canonicalUrl = canonical || `${siteUrl}${normalizePath(pathname)}`;
  const imageUrl = image || `${siteUrl}/logo/og-600x315.jpg`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />
    </Helmet>
  );
};

export default SeoMeta;
